import { useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router';
import { Button, Card, DateCell, useToast } from '@afrilink/ui';
import type { Domain } from '@/domains/domains-registry';
import { domainService } from '@/domains/domains-registry/services/domainService';
import { relativeExpiry } from '@/shared/utils/format';

export default function DomainOverviewTab() {
  const domain = useOutletContext<Domain>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [busy, setBusy] = useState<string | null>(null);

  async function act(label: string, key: string, fn: () => Promise<unknown>): Promise<unknown> {
    setBusy(key);
    try {
      const r = await fn();
      toast({ tone: 'success', title: label });
      return r;
    } catch (e) {
      toast({
        tone: 'danger',
        title: `${label} failed`,
        body: e instanceof Error ? e.message : String(e),
      });
      return null;
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <SectionCard title="Registration">
          <Field label="Domain"        value={domain.name} mono />
          <Field label="TLD"           value={`.${domain.tldFamily}`} />
          <Field label="Registered"    value={<DateCell value={domain.registeredAt} format="long" />} />
          <Field
            label="Expires"
            value={
              <span className="inline-flex items-center gap-1">
                <DateCell value={domain.expiresAt} format="long" /> ({relativeExpiry(domain.expiresAt)})
              </span>
            }
          />
        </SectionCard>

        <SectionCard title="Renewal">
          <Field
            label="Auto-renew"
            value={
              <span className="inline-flex items-center gap-2">
                <strong className="font-medium text-navy-primary">{domain.autoRenew ? 'On' : 'Off'}</strong>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy !== null}
                  loading={busy === 'autorenew'}
                  onClick={() =>
                    act(
                      domain.autoRenew ? 'Auto-renew disabled' : 'Auto-renew enabled',
                      'autorenew',
                      () => domainService.toggleAutoRenew(domain.id, !domain.autoRenew),
                    )
                  }
                >
                  {domain.autoRenew ? 'Disable' : 'Enable'}
                </Button>
              </span>
            }
          />
          <Field
            label="Registry lock"
            value={
              <span className="inline-flex items-center gap-2">
                <strong className="font-medium text-navy-primary">
                  {domain.registryLocked ? 'Locked' : 'Unlocked'}
                </strong>
                <span className="text-xs text-muted-foreground">— managed by support</span>
              </span>
            }
          />
        </SectionCard>
      </div>

      <div className="space-y-6">
        <SectionCard title="Quick actions">
          <div className="space-y-2">
            <Button
              size="sm"
              className="w-full justify-start"
              disabled={busy !== null}
              loading={busy === 'renew'}
              onClick={async () => {
                // DT-1 (2026-05-11): renew routes through a support ticket
                // instead of executing live. Customer lands on the intent
                // processing page where they pay the linked invoice; staff
                // completes the renewal within the SLA.
                const r = (await act('Renewal request received', 'renew', () =>
                  domainService.intentTicket({
                    operation: 'renew',
                    payload: { domain_link_id: domain.id, period_years: 1 },
                  }),
                )) as { ticket_id?: string; redirect_url?: string } | null;
                if (r?.ticket_id) {
                  navigate(r.redirect_url ?? `/app/domains/intent/${r.ticket_id}`);
                }
              }}
            >
              Renew now (1 year)
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start"
              disabled={busy !== null}
              loading={busy === 'epp'}
              onClick={() =>
                act('EPP code emailed to registrant', 'epp', () =>
                  domainService.sendEppCode(domain.id),
                )
              }
            >
              Email me my EPP / auth code
            </Button>
            <Button asChild variant="outline" size="sm" className="w-full justify-start">
              <Link
                to={`/app/support/new?dept=domains&subject=${encodeURIComponent(
                  `Update registrant contacts — ${domain.name}`,
                )}`}
              >
                Update contacts (via support)
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="w-full justify-start">
              <Link to={`/app/domains/${domain.name}/dns`}>Manage DNS records</Link>
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">
            Renewals are issued as invoices in your Billing tab — pay there to complete the renewal.
            Contact updates go through support to honour ZACR's verification rules.
          </p>
        </SectionCard>
      </div>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card interactive={false}>
      <h3 className="border-b border-border px-6 py-3 text-sm font-semibold text-navy-primary">
        {title}
      </h3>
      <div className="space-y-3 px-6 py-5">{children}</div>
    </Card>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-3 gap-4 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={`col-span-2 text-navy-primary ${mono ? 'text-xs tabular-nums' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
